import { Alert, Button, Heading, Icon, List, Paragraph } from '@zillow/constellation-10';
import { IconCheckmarkCircleFilled } from '@zillow/constellation-icons';
import { useFormContext } from 'react-hook-form';
import { ExitReason, IdVerification, TermSet } from '@zg-rentals/id-verification';
import type { LogData, PidAnswers } from '@zg-rentals/id-verification';
import { LogLevel } from '@zg-rentals/id-verification';
import { useState } from 'react';
import {
  GetRentalApplicationDocument,
  GetTermSetDocument,
  TermSetStatus,
  type UserStatus,
} from '../../../../graphql/queries';
import {
  getIdentityExpirationDate,
  getIsIdentityVerified,
  getIsPaymentValid,
  getIsReAuthAllowed,
} from '../../../../utils/user-statuses';
import { useApolloClient } from '@apollo/client';
import { BASE_URL } from '../../../../constants/urls';
import { FIELD_IDS } from '../../../../constants/wizard';
import { BackgroundReportReAuthContent } from '../../../Reports/BackgroundReport/BackgroundReportAlert';
import { useCappsScreeningReportsWebApiHandler_createReports } from '@zg-rentals/ts-capps-client-react-query';
import { REQUESTING_CLIENT } from '../../../../constants/api';
import {
  formatDate,
  getPhoneNumberScreenFormat,
  getPhoneNumberServerFormat,
  getZipCodeScreenFormat,
} from '../../../../utils/formatters';
import { useGetReportsForApplication } from '../../../../hooks/useGetReports';
import { useAuth } from '../../../AuthProvider';
import { getGlobalLogger } from '@zg-rentals/logger-browser';
import { logError } from '@zg-rentals/log-error';
import { useApplicationContext, useApplicationContextOrUndefined } from '../../../../contexts/ApplicationContext';
import { getRentalApplication } from '../../../../analytics/getRentalApplication';
import { trackRentalApplicationEvent } from '../../../../analytics';
import { emitNextClickEvent } from '../../../../analytics/clickstreamEvents';

const logger = getGlobalLogger('applications-renter-web-idv');

export function IDVerificationStep({
  userStatuses,
  onPersist,
}: {
  userStatuses: Array<UserStatus>;
  onPersist: () => Promise<void>;
}) {
  const apolloClient = useApolloClient();
  const createReportsMutation = useCappsScreeningReportsWebApiHandler_createReports();
  const { refetch: refetchReports } = useGetReportsForApplication();
  const applicationContext = useApplicationContext();
  const { termSetStatus } = applicationContext;

  const isIdVerified = getIsIdentityVerified(userStatuses);

  async function onCloseIdvModule() {
    if (getIsPaymentValid(userStatuses)) {
      try {
        await createReportsMutation.mutateAsync({
          postData: {
            requestingClient: REQUESTING_CLIENT,
          },
        });
      } catch (e) {
        logError({ error: e, context: 'IDV - createReportsMutation error' });
      }
    }
    await apolloClient.refetchQueries({ include: [GetRentalApplicationDocument, GetTermSetDocument] });

    refetchReports();
  }

  return !isIdVerified || termSetStatus !== TermSetStatus.current ? (
    <IDVModule onCloseIdvModule={onCloseIdvModule} onPersist={onPersist} />
  ) : (
    <IDVerified userStatuses={userStatuses} onCloseIdvModule={onCloseIdvModule} onPersist={onPersist} />
  );
}

function IDVModule({
  onCloseIdvModule,
  onPersist,
}: {
  onCloseIdvModule: () => Promise<void>;
  onPersist: () => Promise<void>;
}) {
  return (
    <>
      <Heading level={2} textStyle="heading-sm" css={{ marginBottom: 'default', fontFamily: 'heading-alt' }}>
        Verify your identity
      </Heading>
      <Paragraph>
        The information collected in this step is used to verify your identity. This helps ensure that your credit
        history and background report are accurate, secure, and protected. These reports are only shared with rental
        managers who have also verified their identities.
      </Paragraph>
      <Paragraph css={{ marginTop: 'default' }}>You’ll be asked to provide:</Paragraph>
      <List.Root>
        <List.Item>Your full legal name, current address, and mobile phone number</List.Item>
        <List.Item>Your date of birth</List.Item>
        <List.Item>Your Social Security number or Individual Taxpayer Identification Number (optional)</List.Item>
        <List.Item>
          Consent to terms from Experian and Contemporary Information Corp. (CIC) to perform credit and background
          checks
        </List.Item>
      </List.Root>
      <Paragraph css={{ marginTop: 'tighter', marginBottom: 'tighter' }}>
        Experian may also ask additional security questions to help confirm your identity.
      </Paragraph>

      <TriggerIdvButton text="Begin" onCloseIdvModule={onCloseIdvModule} onPersist={onPersist} />
    </>
  );
}

function IDVerified({
  userStatuses,
  onCloseIdvModule,
  onPersist,
}: {
  userStatuses: Array<UserStatus>;
  onCloseIdvModule: () => Promise<void>;
  onPersist: () => Promise<void>;
}) {
  const identityExpirationDate = getIdentityExpirationDate(userStatuses);
  const isReAuthAllowed = getIsReAuthAllowed(userStatuses);

  return (
    <>
      <Icon size="lg" css={{ color: 'text.success' }} render={<IconCheckmarkCircleFilled />} />
      <Heading
        level={2}
        textStyle="heading-sm"
        css={{ marginTop: 'default', marginBottom: 'default', fontFamily: 'heading-alt' }}
      >
        Identity verified
      </Heading>

      {identityExpirationDate && (
        <Paragraph css={{ marginBottom: 'default' }}>
          Your identity verification is valid until {formatDate(identityExpirationDate)}
        </Paragraph>
      )}
      {isReAuthAllowed && (
        <>
          <Alert tone="warning">
            <BackgroundReportReAuthContent />
          </Alert>

          <TriggerIdvButton
            text="Edit information"
            reauthenticate={true}
            onCloseIdvModule={onCloseIdvModule}
            onPersist={onPersist}
          />
        </>
      )}
    </>
  );
}

function TriggerIdvButton({
  text,
  onCloseIdvModule,
  onPersist,
  reauthenticate = false,
}: {
  text: string;
  onCloseIdvModule: () => Promise<void>;
  onPersist: () => Promise<void>;
  reauthenticate?: boolean;
}) {
  const { getValues, setValue } = useFormContext();
  const firstName = getValues(FIELD_IDS.FIRST_NAME);
  const middleName = getValues(FIELD_IDS.MIDDLE_NAME);
  const lastName = getValues(FIELD_IDS.LAST_NAME);
  const phone = getValues(FIELD_IDS.PHONE);
  const currentAddress = getValues(FIELD_IDS.CURRENT_RESIDENCE_STREET);
  const currentCity = getValues(FIELD_IDS.CURRENT_RESIDENCE_CITY);
  const currentState = getValues(FIELD_IDS.CURRENT_RESIDENCE_STATE);
  const currentZip = getValues(FIELD_IDS.CURRENT_RESIDENCE_ZIP);
  const applicationContext = useApplicationContextOrUndefined();
  const { user: userData } = useAuth();
  const [isOpen, setIsOpen] = useState(false);

  async function onExit() {
    await onCloseIdvModule();
  }

  async function onSubmitPidForm(answers: PidAnswers) {
    setValue(FIELD_IDS.FIRST_NAME, answers.firstName);
    setValue(FIELD_IDS.MIDDLE_NAME, answers.middleName);
    setValue(FIELD_IDS.LAST_NAME, answers.lastName);
    setValue(FIELD_IDS.PHONE, getPhoneNumberServerFormat(answers.phone));

    await onPersist();
  }

  function log(logLevel: LogLevel, logData: LogData) {
    const userToken = userData?.userToken;
    const { message, error } = logData;
    switch (logLevel) {
      case LogLevel.Error:
        logger.error({ userId: userToken }, message, error);
        break;
      case LogLevel.Warn:
        logger.warn({ userId: userToken }, message, error);
        break;
      case LogLevel.Info:
        logger.info({ userId: userToken }, message, error);
        break;
      default:
        logger.warn({ userId: userToken }, message, error);
        break;
    }
  }

  function handleOpen() {
    emitNextClickEvent(applicationContext, 'id_verification_provide_details');
    setIsOpen(true);
  }

  function handleClose() {
    setIsOpen(false);
  }

  return (
    <>
      <Button
        tone="brand"
        emphasis="filled"
        css={{ marginTop: 'default', marginBottom: 'default', width: '100%' }}
        type="button"
        onClick={handleOpen}
      >
        {text}
      </Button>
      {isOpen && (
        <IdVerification
          id="applications-renter-web"
          onExit={async ({ reason }) => {
            switch (reason) {
              case ExitReason.AttemptedReAuthWithoutSsn:
                break;
              default:
                await onExit();
                break;
            }
            handleClose();
          }}
          termSet={TermSet.RenterScreening}
          zgApi={BASE_URL}
          pidAnswersForReview={{
            firstName,
            middleName,
            lastName,
            currentAddress,
            currentCity,
            currentState,
            currentZip: getZipCodeScreenFormat(currentZip),
            phone: getPhoneNumberScreenFormat(phone),
          }}
          onSubmitPidForm={onSubmitPidForm}
          skipLandingStep
          trackEvent={(basePayload) => {
            trackRentalApplicationEvent({
              ...basePayload,
              ...getRentalApplication(applicationContext),
            });
          }}
          reauthenticate={reauthenticate}
          handleScreeningReports={true}
          log={log}
        />
      )}
    </>
  );
}
